/**
 * 설정 › AI 에이전트.
 *
 * 계정은 사용자 본인 것이다. 세 가지 방법이 있고 **로그인이 기본**이다:
 *   1) 로그인 — 서버가 claude auth login 을 대신 몰고, 사용자는 자기 브라우저에서 승인한
 *      코드만 넣는다. 터미널도, PC 에 CLI 설치도 필요 없고, 만료되면 자동 갱신된다.
 *   2) 토큰 — `claude setup-token` 출력을 붙여넣는다. 브라우저를 못 여는 환경(자동화·폐쇄망)용.
 *   3) 내 API 키 — 구독 대신 종량 과금. 제공자 콘솔에서 발급한 키를 넣는다.
 * 2·3 은 서버가 AES-256-GCM 으로 암호화해 보관한다 — 키는 브라우저에 남지 않는다.
 *
 * **어느 것이 쓰이는지는 서버가 말해 준다**(account.source). 여기서 다시 판정하지 않는다 —
 * 두 곳에서 판정하면 화면과 실제 과금 주체가 갈라진다. 경로를 **고정**하면 그것이 없을 때
 * 실행이 실패한다(다른 경로로 넘어가지 않는다).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FiCheckCircle, FiAlertCircle, FiRefreshCw, FiLogIn, FiLogOut, FiExternalLink, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import * as api from '../../services/assistantClient';
import { TUNING_LEVELS, normalizeTuningLevel } from '../../utils/tuningLevels';
import { loginPollStep, LOGIN_POLL_MS, LOGIN_WAIT_MAX_MS } from '../../utils/loginPoll';

const PROVIDERS = [
    { value: 'claude', label: 'Claude (Claude Code)', enabled: true },
    { value: 'codex', label: 'Codex (ChatGPT)', enabled: true },
    { value: 'grok', label: 'Grok', enabled: false },
];
// 제공자마다 모델 이름 체계가 다르다. 'default' 는 그 CLI 의 기본값을 그대로 쓴다는 뜻.
// 사용자의 스킬 수준. value 는 **백엔드 skillLevel.js 의 값과 같아야 한다** — 번역하지 않는다.
const SKILL_LEVELS = ['novice', 'developer', 'expert'];

// 모델 이름은 여기에 적지 않는다 — 제공자에게 물은 목록을 쓴다(api.getModels). 적어 두면 모델이 바뀐 뒤에도 옛 이름만 고르게 된다.
/** 직접 적는 모델 이름의 모양 — 백엔드 harness/models.js 의 MODEL_RE 와 같아야 한다(어긋나면 화면은 받는데 실행에서 거부된다) */
const MODEL_RE = /^[A-Za-z0-9][A-Za-z0-9._:\-[\]]{0,79}$/;
const CUSTOM = '__custom__';

function AssistantSettingsSection({ settings, setSettings, isStandalone }) {
    const { t } = useTranslation();
    const [account, setAccount] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [login, setLogin] = useState(null);      // { loginId, url }
    const [code, setCode] = useState('');
    const [token, setToken] = useState('');
    const [tokenOpen, setTokenOpen] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [apiKeyOpen, setApiKeyOpen] = useState(false);
    const [loginError, setLoginError] = useState(null);
    const patch = (p) => setSettings(prev => ({ ...prev, ...p }));
    // 고를 수 있는 모델 — 제공자·계정마다 다르다. { models, source: live|bundled|none, reason }
    const [modelInfo, setModelInfo] = useState(null);
    const [modelBusy, setModelBusy] = useState(false);
    const [customModel, setCustomModel] = useState(null);     // 직접 입력 중인 글(null = 입력 칸 닫힘)

    const refresh = useCallback(async () => {
        try {
            const a = await api.getAccount(settings.provider || 'claude');
            setAccount(a);
            setLoadError(null);
            if (a.pendingLogin) setLogin(a.pendingLogin);
        } catch (err) {
            setAccount(null);
            setLoadError(err.message);
        }
    }, [settings.provider]);

    useEffect(() => { refresh(); }, [refresh]);

    const beginLogin = async () => {
        // 클릭 **그 순간**에 빈 탭을 연다. await 뒤에 window.open 을 부르면 사용자 제스처가 끝난
        // 뒤라 브라우저가 조용히 막아 버리고, 화면에는 아무 일도 안 일어난 것처럼 보인다.
        let tab = null;
        try { tab = window.open('', '_blank'); } catch (_e) { tab = null; }
        setBusy(true);
        setLoginError(null);
        try {
            const r = await api.startLogin(settings.provider || 'claude');
            setLogin(r);
            setCode('');
            if (tab && !tab.closed) tab.location.href = r.url;
        } catch (err) {
            if (tab && !tab.closed) tab.close();
            setLoginError(err.message);     // 토스트는 모달 뒤로 가려 놓치기 쉽다 — 화면에도 남긴다
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    // 코드를 되돌려주지 않는 흐름(Codex 디바이스 인증)은 CLI 가 스스로 승인 완료를 감지한다.
    // 요청을 몇 분씩 붙들지 않도록 화면이 계정 상태만 짧게 폴링한다.
    //
    // **멈춰야 할 때 멈춘다**(utils/loginPoll.js). 전에는 요청 실패를 조용히 넘겨, 대기 중에 LiveSQL 로그인이
    // 끊기자 401 을 받으면서도 3초마다 영원히 불렀다 — QBase 성능관리의 에러 트레이스가 이 요청으로 가득 찼다
    // (신고 2026-09-14). 앞 요청이 끝난 뒤에 다음을 잡는다(setInterval 은 느린 응답 위에 요청을 겹쳐 쌓는다).
    useEffect(() => {
        if (!login || login.needsCode) return undefined;
        const provider = settings.provider || 'claude';
        const startedAt = Date.now();
        let stop = false;
        let timer = null;
        let failures = 0;

        const end = (message) => {
            stop = true;
            setLogin(null);
            if (message) setLoginError(message);        // 토스트는 모달 뒤로 가려 놓치기 쉽다 — 화면에 남긴다
        };

        const tick = async () => {
            if (stop) return;
            let account = null;
            let error = null;
            try { account = await api.getAccount(provider); } catch (err) { error = err; }
            if (stop) return;
            if (account) setAccount(account);
            const step = loginPollStep({ account, error, failures, startedAt });
            failures = step.failures;
            switch (step.action) {
                case 'done':
                    end(null);
                    toast.success(account.email ? t('assistant:settings.loginDoneAs', { email: account.email }) : t('assistant:settings.loginDone'));
                    return;
                case 'failed':
                    end(null);
                    toast.error((account.pendingLogin && account.pendingLogin.message) || t('assistant:settings.loginFailed'));
                    return;
                case 'gone':
                    end(null);
                    return;
                case 'sessionExpired':
                    // LiveSQL 로그인이 끊겼다 — 서버 쪽 대기를 치우러 가도 같은 401 이다. 다시 로그인하라고만 알린다
                    end(t('assistant:settings.loginPollSessionExpired'));
                    return;
                case 'timeout':
                    end(t('assistant:settings.loginPollTimeout', { minutes: LOGIN_WAIT_MAX_MS / 60000 }));
                    // 서버가 붙들고 있는 로그인 흐름도 치운다 — 남으면 다음 시도가 옛 대기를 이어받는다
                    api.cancelLogin().catch(() => { /* 치우지 못해도 화면은 멈춘다 */ });
                    return;
                case 'errors':
                    end(t('assistant:settings.loginPollErrors', { message: (error && error.message) || '' }));
                    return;
                default:
                    timer = setTimeout(tick, LOGIN_POLL_MS);
            }
        };

        timer = setTimeout(tick, LOGIN_POLL_MS);
        return () => { stop = true; clearTimeout(timer); };
    }, [login, settings.provider, t]);

    const finishLogin = async () => {
        if (!login || !code.trim()) return;
        setBusy(true);
        try {
            const r = await api.submitLoginCode(login.loginId, code.trim());
            setLogin(null);
            setCode('');
            toast.success(r.email ? t('assistant:settings.loginDoneAs', { email: r.email }) : t('assistant:settings.loginDone'));
            await refresh();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    const abortLogin = async () => {
        setLogin(null);
        setCode('');
        try { await api.cancelLogin(); } catch (_e) { /* 서버가 정리 못 해도 화면은 되돌린다 */ }
    };

    const doLogout = async () => {
        setBusy(true);
        try {
            await api.logout(settings.provider || 'claude');
            toast.info(t('assistant:settings.loggedOut'));
            await refresh();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    const saveToken = async () => {
        if (!token.trim()) return;
        setBusy(true);
        try {
            await api.putCredential(token.trim(), settings.provider || 'claude');
            setToken('');
            toast.success(t('assistant:settings.tokenSaved'));
            await refresh();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    const removeToken = async () => {
        setBusy(true);
        try {
            await api.deleteCredential(settings.provider || 'claude');
            toast.info(t('assistant:settings.tokenDeleted'));
            await refresh();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    const saveApiKey = async () => {
        if (!apiKey.trim()) return;
        setBusy(true);
        try {
            await api.saveApiKey(apiKey.trim(), settings.provider || 'claude');
            setApiKey('');                       // 화면에도 남기지 않는다
            toast.success(t('assistant:settings.apiKeySaved'));
            await refresh();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    const removeApiKey = async () => {
        setBusy(true);
        try {
            await api.deleteApiKey(settings.provider || 'claude');
            toast.info(t('assistant:settings.apiKeyDeleted'));
            await refresh();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    const pinSource = async (value) => {
        setBusy(true);
        try {
            await api.setAuthSource(value, settings.provider || 'claude');
            toast.success(t('assistant:settings.sourceSaved'));
            await refresh();
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(false); }
    };

    // 모드는 **서버가 정한다**. 프론트 빌드 플래그(isStandalone)는 서버가 응답하기 전까지의 추정일 뿐이라,
    // 둘이 어긋나면(스탠드얼론 빌드로 웹 백엔드를 보는 개발 구성 등) 토큰 항목이 잘못 숨는다.
    const webMode = account ? account.mode === 'web' : !isStandalone;
    // 키 형식 예시와 발급 페이지는 **서버가 준다**. 화면에 적어 두면 서버와 갈라진다.
    const keyProvider = (account && (account.apiKeyProviders || []).find(p => p.id === (settings.provider || 'claude'))) || {};
    const sources = (account && account.authSources) || [];
    const loggedIn = !!(account && account.loggedIn);
    const provider = settings.provider || 'claude';
    const loadModels = useCallback(async (refresh) => {
        setModelBusy(true);
        try { setModelInfo(await api.getModels(provider, refresh)); } catch (_e) { setModelInfo({ models: [], source: 'none', reason: 'FAILED' }); } finally { setModelBusy(false); }
    }, [provider]);
    // 제공자를 바꾸거나 로그인 상태가 바뀌면 다시 받는다(계정이 바뀌면 목록도 달라질 수 있다 — 그때는 새로 묻는다)
    const wasLoggedIn = useRef(null);
    useEffect(() => {
        const changed = wasLoggedIn.current !== null && wasLoggedIn.current !== loggedIn;
        wasLoggedIn.current = loggedIn;
        setCustomModel(null);
        loadModels(changed);
    }, [loadModels, loggedIn]);
    const modelList = (modelInfo && modelInfo.models) || [];
    const curModel = settings.model || 'default';
    // 지금 고른 모델이 목록에 없다 — 지우지 않고 그대로 보이되 그 사실을 말한다(직접 적은 것이거나 제공자가 내린 것이다)
    const offList = curModel !== 'default' && !modelList.some(m => m.id === curModel);
    const customOk = customModel !== null && MODEL_RE.test(customModel.trim());
    const bySource = account && account.source;
    const SOURCE_TEXT = {
        login: 'assistant:settings.viaLogin',
        token: 'assistant:settings.viaToken',
        apiKey: 'assistant:settings.viaApiKey',
    };
    const statusText = loadError ? loadError
        : !account ? ''
            : loggedIn
                ? [
                    t(SOURCE_TEXT[bySource] || 'assistant:settings.viaLogin'),
                    account.email || null,
                    bySource === 'token' ? account.hint : null,
                    bySource === 'apiKey' ? account.apiKeyHint : null,
                ].filter(Boolean).join(' · ')
                : account.blockedSource
                    ? t('assistant:settings.sourceBlocked')
                    : t('assistant:settings.accountNone');

    return (
        <section>
            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:settings.provider')}</span>
                    <span className="stg-hint">{t('assistant:settings.providerHint')}</span>
                </div>
                <select
                    className="stg-select"
                    value={settings.provider || 'claude'}
                    // 제공자를 바꾸면 모델은 기본값으로 — 다른 제공자의 모델 이름이 남으면 실행에서 거부된다
                    onChange={(e) => patch({ provider: e.target.value, model: 'default' })}
                >
                    {PROVIDERS.map(p => (
                        <option key={p.value} value={p.value} disabled={!p.enabled}>
                            {p.enabled ? p.label : `${p.label} (${t('assistant:settings.v2')})`}
                        </option>
                    ))}
                </select>
            </div>

            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:settings.model')}</span>
                </div>
                <span className="stg-model">
                    <select className="stg-select" value={customModel !== null ? CUSTOM : curModel} aria-label={t('assistant:settings.model')}
                        onChange={(e) => {
                            if (e.target.value === CUSTOM) { setCustomModel(offList ? curModel : ''); return; }
                            setCustomModel(null);
                            patch({ model: e.target.value });
                        }}>
                        <option value="default">{t('assistant:settings.modelDefault')}</option>
                        {modelList.map(m => <option key={m.id} value={m.id} title={m.description || undefined}>{m.label && m.label !== m.id ? `${m.label} (${m.id})` : m.id}</option>)}
                        {offList ? <option value={curModel}>{t('assistant:settings.modelOffList', { model: curModel })}</option> : null}
                        <option value={CUSTOM}>{t('assistant:settings.modelCustom')}</option>
                    </select>
                    <button type="button" className="app-btn" disabled={modelBusy} onClick={() => loadModels(true)} title={t('assistant:settings.modelRefresh')} aria-label={t('assistant:settings.modelRefresh')}>
                        <FiRefreshCw size={12} />
                    </button>
                </span>
            </div>
            {customModel !== null && (
                <div className="stg-row">
                    <div className="stg-row-main"><span className="stg-hint">{t('assistant:settings.modelCustomHint')}</span></div>
                    <span className="stg-model">
                        <input className="stg-text" value={customModel} maxLength={80} placeholder={t('assistant:settings.modelCustomPh')} aria-label={t('assistant:settings.modelCustom')}
                            onChange={(e) => setCustomModel(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && customOk) { patch({ model: customModel.trim() }); setCustomModel(null); } }} />
                        <button type="button" className="app-btn" disabled={!customOk} onClick={() => { patch({ model: customModel.trim() }); setCustomModel(null); }}>{t('assistant:settings.modelCustomApply')}</button>
                    </span>
                </div>
            )}
            {/* 목록이 어디서 왔는지 — 못 받았으면 그렇다고 말한다(적어 둔 이름으로 조용히 넘어가지 않는다) */}
            {modelInfo && (modelInfo.source !== 'live' || offList) ? (
                <div className="stg-row">
                    <div className="stg-row-main">
                        <span className="stg-hint">
                            {modelInfo.source === 'none' && loggedIn ? t('assistant:settings.modelsNone') : null}
                            {modelInfo.source === 'none' && !loggedIn ? t('assistant:settings.modelsNeedLogin') : null}
                            {modelInfo.source === 'bundled' ? t('assistant:settings.modelsBundled') : null}
                            {offList && modelInfo.source !== 'none' ? ` ${t('assistant:settings.modelOffListHint', { model: curModel })}` : null}
                        </span>
                    </div>
                </div>
            ) : null}

            {/* Codex 원격(웹)은 장치 코드 인증을 계정에서 켜 둔 경우에만 된다. 눌러 보고 실패하기 전에 알린다. */}
            {webMode && (settings.provider === 'codex') && !loggedIn && (
                <div className="stg-row">
                    <div className="stg-row-main">
                        <span className="stg-hint">{t('assistant:settings.codexDevicePrereq')}</span>
                    </div>
                </div>
            )}

            {/* 계정 — 상태 + 로그인/로그아웃 */}
            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:settings.account')}</span>
                    <span className="stg-hint">{statusText}</span>
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {loggedIn ? <FiCheckCircle size={14} color="#16a34a" /> : <FiAlertCircle size={14} color="#d97706" />}
                    {loggedIn && bySource === 'login' ? (
                        <button type="button" className="app-btn" onClick={doLogout} disabled={busy}>
                            <FiLogOut size={12} /> {t('assistant:settings.logout')}
                        </button>
                    ) : (
                        <button type="button" className="app-btn app-btn-primary" onClick={beginLogin} disabled={busy}>
                            <FiLogIn size={12} />{' '}
                            {busy ? t('assistant:settings.loginStarting')
                                : login ? t('assistant:settings.loginRestart')
                                    : t('assistant:settings.login')}
                        </button>
                    )}
                    <button type="button" className="app-btn" onClick={refresh} disabled={busy} title={t('assistant:settings.refresh')}>
                        <FiRefreshCw size={12} />
                    </button>
                </span>
            </div>

            {loginError && (
                <div className="stg-row">
                    <div className="stg-row-main">
                        <span className="stg-hint" style={{ color: '#b91c1c' }}>{loginError}</span>
                    </div>
                </div>
            )}

            {/* 로그인 진행 중 — 링크와 코드 입력 */}
            {login && (
                <div className="stg-row" style={{ display: 'block' }}>
                    <div className="stg-row-main" style={{ marginBottom: 6 }}>
                        <span className="stg-label">{t('assistant:settings.loginStep')}</span>
                        <span className="stg-hint">
                            {login.flow === 'code' ? t('assistant:settings.loginStepHint')
                                : login.flow === 'device' ? t('assistant:settings.loginStepHintDevice')
                                    : t('assistant:settings.loginStepHintCallback')}
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <a className="app-btn app-btn-primary" href={login.url} target="_blank" rel="noopener noreferrer">
                            <FiExternalLink size={12} /> {t('assistant:settings.loginOpen')}
                        </a>
                        {login.needsCode ? (
                            <>
                                <input
                                    className="stg-text"
                                    value={code}
                                    placeholder={t('assistant:settings.codePlaceholder')}
                                    autoComplete="off"
                                    style={{ width: 260 }}
                                    onChange={(e) => setCode(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); finishLogin(); } }}
                                />
                                <button type="button" className="app-btn app-btn-primary" onClick={finishLogin} disabled={busy || !code.trim()}>
                                    {t('assistant:settings.loginFinish')}
                                </button>
                            </>
                        ) : (
                            <>
                                {/* 디바이스 인증이면 이 코드를 사용자가 그 페이지에 입력한다. 콜백 방식이면 보여줄 코드가 없다.
                                    어느 쪽이든 우리에게 되돌려줄 것은 없고, 완료는 계정 상태 폴링으로 알아챈다. */}
                                {login.code ? <code className="stg-code-chip">{login.code}</code> : null}
                                <span className="stg-hint">{t('assistant:settings.loginWaiting')}</span>
                            </>
                        )}
                        <button type="button" className="app-btn" onClick={abortLogin} disabled={busy}>
                            {t('assistant:settings.loginCancel')}
                        </button>
                    </div>
                </div>
            )}

            {/* 토큰 — 보조 수단. 접어 둔다. */}
            {webMode && (!account || account.supportsToken !== false) && (
                <>
                    <div className="stg-row stg-row-clickable" onClick={() => setTokenOpen(o => !o)} role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setTokenOpen(o => !o); }}>
                        <div className="stg-row-main">
                            <span className="stg-label">
                                {tokenOpen ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />} {t('assistant:settings.tokenLabel')}
                            </span>
                            <span className="stg-hint">
                                {account && account.hasToken ? t('assistant:settings.tokenRegistered', { hint: account.hint || '' }) : t('assistant:settings.tokenAlt')}
                            </span>
                        </div>
                    </div>
                    {tokenOpen && (
                        <div className="stg-row">
                            <div className="stg-row-main">
                                <span className="stg-hint">{t('assistant:settings.tokenHint')}</span>
                            </div>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <input
                                    className="stg-text"
                                    type="password"
                                    value={token}
                                    placeholder="sk-ant-oat…"
                                    autoComplete="off"
                                    onChange={(e) => setToken(e.target.value)}
                                />
                                <button type="button" className="app-btn" onClick={saveToken} disabled={busy || !token.trim()}>
                                    {t('assistant:settings.tokenSave')}
                                </button>
                                <button type="button" className="app-btn" onClick={removeToken} disabled={busy || !(account && account.hasToken)}>
                                    {t('assistant:settings.tokenDelete')}
                                </button>
                            </span>
                        </div>
                    )}
                </>
            )}

            {/* 내 API 키 — 구독 대신 종량. 토큰과 **같이** 등록해 둘 수 있다(서로를 지우지 않는다). */}
            {webMode && account && account.supportsApiKey && (
                <>
                    <div className="stg-row stg-row-clickable" onClick={() => setApiKeyOpen(o => !o)} role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setApiKeyOpen(o => !o); }}>
                        <div className="stg-row-main">
                            <span className="stg-label">
                                {apiKeyOpen ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />} {t('assistant:settings.apiKeyLabel')}
                            </span>
                            <span className="stg-hint">
                                {account.hasApiKey
                                    ? t('assistant:settings.apiKeyRegistered', { hint: account.apiKeyHint || '' })
                                    : t('assistant:settings.apiKeyAlt')}
                            </span>
                        </div>
                    </div>
                    {apiKeyOpen && (
                        <div className="stg-row">
                            <div className="stg-row-main">
                                <span className="stg-hint">{t('assistant:settings.apiKeyHint', { name: keyProvider.name })}</span>
                                {/* 발급 페이지 — 무료 등급 키를 받으러 갈 곳을 알려 준다 */}
                                <a className="stg-hint" href={keyProvider.docsUrl} target="_blank" rel="noreferrer noopener">
                                    {t('assistant:settings.apiKeyIssue')} <FiExternalLink size={11} />
                                </a>
                            </div>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <input
                                    className="stg-text"
                                    type="password"
                                    value={apiKey}
                                    placeholder={keyProvider.placeholder}
                                    autoComplete="off"
                                    onChange={(e) => setApiKey(e.target.value)}
                                />
                                <button type="button" className="app-btn" onClick={saveApiKey} disabled={busy || !apiKey.trim()}>
                                    {t('assistant:settings.tokenSave')}
                                </button>
                                <button type="button" className="app-btn" onClick={removeApiKey} disabled={busy || !account.hasApiKey}>
                                    {t('assistant:settings.tokenDelete')}
                                </button>
                            </span>
                        </div>
                    )}
                </>
            )}

            {/* 인증 경로 — 고를 것이 둘 이상일 때만 묻는다. 하나뿐이면 물어볼 것이 없다. */}
            {webMode && account && (sources.length > 1 || account.pinnedSource) && (
                <div className="stg-row">
                    <div className="stg-row-main">
                        <span className="stg-label">{t('assistant:settings.sourceLabel')}</span>
                        <span className="stg-hint">{t('assistant:settings.sourceHint')}</span>
                    </div>
                    <select
                        className="stg-select"
                        value={account.pinnedSource || 'auto'}
                        disabled={busy}
                        onChange={(e) => pinSource(e.target.value)}
                    >
                        <option value="auto">{t('assistant:settings.sourceAuto')}</option>
                        {api.AUTH_SOURCES.map(v => (
                            <option key={v} value={v}>{t(`assistant:settings.source${v.charAt(0).toUpperCase()}${v.slice(1)}`)}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* 데이터모델·표준사전 — 웹 모드(QBase 세션)에서만 의미가 있다. 비워 두면 에이전트가 목록에서 고르거나 되묻는다. */}
            {webMode && (
                <>
                    <div className="stg-row">
                        <div className="stg-row-main">
                            <span className="stg-label">{t('assistant:settings.stdNm')}</span>
                            <span className="stg-hint">{t('assistant:settings.stdNmHint')}</span>
                        </div>
                        <input
                            className="stg-text"
                            value={settings.stdNm || ''}
                            placeholder={t('assistant:settings.stdNmPlaceholder')}
                            onChange={(e) => patch({ stdNm: e.target.value })}
                        />
                    </div>
                    <div className="stg-row">
                        <div className="stg-row-main">
                            <span className="stg-label">{t('assistant:settings.dataModelId')}</span>
                            <span className="stg-hint">{t('assistant:settings.dataModelIdHint')}</span>
                        </div>
                        <input
                            className="stg-text"
                            value={settings.dataModelId || ''}
                            placeholder="MDL0000"
                            onChange={(e) => patch({ dataModelId: e.target.value.trim() })}
                        />
                    </div>
                </>
            )}

            <label className="stg-row stg-row-clickable">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:settings.egressComments')}</span>
                    <span className="stg-hint">{t('assistant:settings.egressCommentsHint')}</span>
                </div>
                <input type="checkbox" checked={settings.egressComments !== false} onChange={(e) => patch({ egressComments: e.target.checked })} />
            </label>

            <label className="stg-row stg-row-clickable">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:settings.egressSampleRows')}</span>
                    <span className="stg-hint">{t('assistant:settings.egressSampleRowsHint')}</span>
                </div>
                <input type="checkbox" checked={!!settings.egressSampleRows} onChange={(e) => patch({ egressSampleRows: e.target.checked })} />
            </label>

            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:settings.skillLevel')}</span>
                    <span className="stg-hint">{t(`assistant:skill.${settings.skillLevel || 'developer'}Hint`)}</span>
                </div>
                <select
                    className="stg-select"
                    value={settings.skillLevel || 'developer'}
                    onChange={(e) => patch({ skillLevel: e.target.value })}
                >
                    {SKILL_LEVELS.map(lv => (
                        <option key={lv} value={lv}>{t(`assistant:skill.${lv}`)}</option>
                    ))}
                </select>
            </div>

            {/* **스킬 수준과 다른 축이다.** 위는 답의 형태, 이것은 어디까지 해도 되는지의 상한.
                요청 직전에도 바꿀 수 있게 패널·SQL 튜닝 탭에도 같은 선택이 있고, 값은 여기 하나다. */}
            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:tuningLevel.label')}</span>
                    <span className="stg-hint">{t(`assistant:tuningLevel.${normalizeTuningLevel(settings.tuningLevel)}Hint`)}</span>
                </div>
                <select
                    className="stg-select"
                    value={normalizeTuningLevel(settings.tuningLevel)}
                    onChange={(e) => patch({ tuningLevel: Number(e.target.value) })}
                >
                    {TUNING_LEVELS.map(lv => (
                        <option key={lv} value={lv}>{t('assistant:tuningLevel.option', { n: lv, name: t(`assistant:tuningLevel.${lv}`) })}</option>
                    ))}
                </select>
            </div>

            <div className="stg-row">
                <div className="stg-row-main">
                    <span className="stg-label">{t('assistant:settings.askTimeout')}</span>
                    <span className="stg-hint">{t('assistant:settings.askTimeoutHint')}</span>
                </div>
                <input
                    className="stg-number"
                    type="number"
                    min={1}
                    max={30}
                    value={settings.askTimeoutMin || 10}
                    onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isFinite(n)) patch({ askTimeoutMin: Math.max(1, Math.min(30, n)) });
                    }}
                />
            </div>
        </section>
    );
}

AssistantSettingsSection.propTypes = {
    settings: PropTypes.object.isRequired,
    setSettings: PropTypes.func.isRequired,
    isStandalone: PropTypes.bool,
};

export default AssistantSettingsSection;
